import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { UserService, ProductService, WindowService, Download, RawProductData, environment } from "@mypxplat/xplat/core";
import { DownloadTileBaseComponent } from "@mypxplat/xplat/features";
import { FirmwareInstructionsComponent } from "../modals";
import { AppService, AvoService } from "@mypxplat/xplat/web/core";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "myp-download-tile",
  templateUrl: "download-tile.component.html",
  standalone: false,
})
export class DownloadTileComponent extends DownloadTileBaseComponent {
  @Input() dealer: boolean = false;
  @Input() download: any;
  @Input() product_id: number;
  @Input() product: RawProductData;

  public checkingFirmwareInstructions = false;

  constructor(
    userService: UserService,
    public appService: AppService,
    public productService: ProductService,
    public avoService: AvoService,
    private _win: WindowService,
    public translate: TranslateService,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService);
  }

  /**
   * Which id the firmware-instructions endpoint needs, which depends on who's asking:
   * - Dealers call it with `isDealerProduct=true`, keyed on the PRODUCT id (no ownership check).
   * - Owners call it with the LICENSE id, which product-detail injects onto each Firmware item
   *   from the user's licenses; paeapi checks ownership against it.
   * The public downloads page has neither (no license context, not a dealer), so `download.licenseId`
   * is undefined there — requesting `/product/firmware-instructions/undefined` was a 422 that left
   * the modal spinning. With no usable id there is nothing to look up, so hand over the file.
   */
  get firmwareInstructionsId(): number | undefined {
    return this.dealer ? this.product_id || this.product?.id : this.download?.licenseId;
  }

  get downloadsFirmwareDirectly(): boolean {
    return !this.firmwareInstructionsId || !this.download?.version;
  }

  /**
   * Shows the instructions modal when the product has instructions; otherwise — no instructions,
   * no usable id, or the check itself failing — downloads the firmware file directly, so the
   * user always ends up with the firmware.
   */
  showFirmwareInstructions() {
    if (this.downloadsFirmwareDirectly) {
      this.downloadTokenizedAsset();
      return;
    }
    if (this.checkingFirmwareInstructions) return;

    const productId = this.firmwareInstructionsId;
    const version = this.download.version;

    this.checkingFirmwareInstructions = true;
    this.productService.getFirmwareInstructions(productId, version, this.dealer).subscribe({
      next: (result: any) => {
        this.checkingFirmwareInstructions = false;
        this._cdr.markForCheck();
        if (result?.hasInstructions && result?.content) {
          const modalRef = this.appService.showModal(FirmwareInstructionsComponent, { size: "lg" });
          modalRef.componentInstance.content = result.content;
          modalRef.componentInstance.loadingContent = false;
          modalRef.componentInstance.productDetails = this.product;
          modalRef.componentInstance.firmware = {
            product_id: productId,
            version,
            dealer: this.dealer,
          };
        } else {
          this.downloadTokenizedAsset();
        }
      },
      error: () => {
        this.checkingFirmwareInstructions = false;
        this._cdr.markForCheck();
        this.downloadTokenizedAsset();
      },
    });
  }

  downloadTokenizedAsset() {
    if (this.download.tokenized || this.download.type == "firmware") {
      this.productService
        .getTokenizedUrl(
          this.download.folder,
          this.download.fileName || this.download.filename,
          this.userService.appId == "mypresonus" ? "mypresonus" : "studio_one_plus",
          this.product.title,
          this.product.id.toString(),
          this.download.fileName || this.download.filename,
          this.download.contentLength || this.download.filesize,
          this.download.version || this.download.version_number
        )
        .subscribe({
          next: (result: any) => {
            window.open(result.url);
          },
          error: (error) => {
            this.appService.alertError(error);
          },
        });
    } else {
      this._win.openUrl(this.download.url);
    }
    this.maybePromptMarketingOptIn();
  }

  private maybePromptMarketingOptIn() {
    if (this.product?.id !== environment.product_ids.fsp8demo) return;
    const settings = this.user.fender_email_settings || {
      fender_news: false,
      fender_news_intl: false,
      fender_studio_news: false,
      presonus_news: false,
    };
    const fenderNewsOn = this.user.country === "US" ? settings.fender_news : settings.fender_news_intl;
    if (fenderNewsOn && settings.fender_studio_news) return;

    this.appService
      .confirm(this.translate.instant("profile.demo.marketing_optin_fender_news"), this.translate.instant("onboarding.signup"), this.translate.instant("general.no_thanks"))
      .then((confirmed) => {
        if (!confirmed) return;
        const args: any = { ...settings, fender_studio_news: true };
        if (this.user.country === "US") {
          args.fender_news = true;
        } else {
          args.fender_news_intl = true;
        }
        this.userService.saveIterableSettings(args).subscribe(() => {
          this.userService.getUserDetails(false).subscribe();
        });
      });
  }
}
